import { useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import GeoContext from 'contexts/geoContext'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import HotelListingDrawerHeader from './HotelListingDrawerHeader'
import DrawerHotelTile from './DrawerHotelTile'
import DrawerHotelTileSkeleton from './DrawerHotelTileSkeleton'
import HotelListingEmptyState from './HotelListingEmptyState'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import HotelDetailView from './HotelDetailView'
import useHotelSearch from './useHotelSearch'
import { mapOfferDetailToDrawerHotel } from './mapOfferDetailToDrawerHotel'
import type { HotelListingDrawerProps, DrawerHotel } from './types'
import DrawerAskAIFooter from 'tripPlanner/components/DrawerAskAI/DrawerAskAIFooter'
import useOfferDetails from '../../hooks/useOfferDetails'
import useOfferReviews from '../../hooks/useOfferReviews'
import { isOfferBookedInItinerary, getOfferItineraryDayRange, getOfferOrderId } from 'tripPlanner/utils/chatItinerary'

const NonInteractiveChipWrapper = styled.div`
  pointer-events: none;
`

const SKELETON_COUNT = 3

function formatDrawerHotelInfo(hotel: DrawerHotel): string {
  const lines: Array<string> = []
  lines.push(`Hotel: ${hotel.name}`)
  if (hotel.location) {
    lines.push(`Location: ${hotel.location}`)
  }
  if (hotel.description) {
    lines.push(`Description: ${hotel.description.substring(0, 500)}${hotel.description.length > 500 ? '...' : ''}`)
  }
  if (hotel.rating) {
    const reviewCount = hotel.reviewsTotal ? ` (${hotel.reviewsTotal} reviews)` : ''
    lines.push(`Rating: ${hotel.rating}/5${reviewCount}`)
  }
  if (hotel.price) {
    const currency = hotel.price.currency ?? 'AUD'
    lines.push(`Price: ${currency} ${hotel.price.total} for ${hotel.price.nights} night${hotel.price.nights !== 1 ? 's' : ''}`)
  }
  if (hotel.inclusions && hotel.inclusions.length > 0) {
    const inclusionsList = hotel.inclusions.slice(0, 5).map(i => i.description).join(', ')
    lines.push(`Inclusions: ${inclusionsList}`)
  }
  if (hotel.amenities && hotel.amenities.length > 0) {
    const amenitiesList = hotel.amenities.slice(0, 8).map(a => a.name).join(', ')
    lines.push(`Amenities: ${amenitiesList}`)
  }
  lines.push(`Offer ID: ${hotel.offerId}`)
  return lines.join('\n')
}

const FullWidthLoadingBox = styled(LoadingBox)`
  width: 100%;
`

export const ContentWrapper = styled.div`
  margin: 0 auto;
  padding: ${rem(24)};
  width: ${rem(700)};
  max-width: 100%;
  box-sizing: border-box;
`

function HotelListingDrawer({
  isOpen,
  onClose,
  placeName,
  placeCountry,
  stopStartDate,
  stopEndDate,
  onShowMap,
  onExploreMap,
  itineraryData,
  onSendMessage,
}: HotelListingDrawerProps) {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null)
  const [selectedOfferType, setSelectedOfferType] = useState<string | undefined>(undefined)
  const searchQuery = `${placeName}, ${placeCountry}`

  // Stop dates are the default checkIn/checkOut for search + drawer
  const checkIn = stopStartDate
  const checkOut = stopEndDate

  const { hotels, isLoading } = useHotelSearch({
    searchQuery,
    region: geo.currentRegionCode,
    checkIn,
    checkOut,
    enabled: isOpen,
  })

  const { offerDetail, isLoading: isDetailLoading, isError: isDetailError, refetch: refetchDetail } = useOfferDetails({
    offerId: selectedOfferId ?? undefined,
    region: geo.currentRegionCode,
    offerType: selectedOfferType,
    checkIn,
    checkOut,
    enabled: !!selectedOfferId,
  })

  const { reviews, isLoading: reviewsLoading } = useOfferReviews({
    offerId: selectedOfferId ?? undefined,
    region: geo.currentRegionCode,
    enabled: !!selectedOfferId && !!offerDetail,
  })

  const detailHotel = useMemo(() => {
    if (!offerDetail) return undefined
    return mapOfferDetailToDrawerHotel(offerDetail)
  }, [offerDetail])

  const handleSelectHotel = useCallback((hotel: DrawerHotel) => {
    setSelectedOfferId(hotel.offerId)
    setSelectedOfferType(hotel.offerType)
  }, [])

  const handleBack = useCallback(() => {
    setSelectedOfferId(null)
  }, [])

  const handleClose = useCallback(() => {
    setSelectedOfferId(null)
    onClose()
  }, [onClose])

  const handleAfterClose = useCallback(() => {
    setSelectedOfferId(null)
  }, [])

  const browseAllResultsUrl = `/${geo.currentRegionCode}/search?destinationName=${encodeURIComponent(placeName)}`
  const hasHotels = hotels.length > 0
  const isDetailView = !!selectedOfferId

  const handleShowMapFromHeader = useCallback(() => {
    if (detailHotel) {
      onShowMap(detailHotel)
    }
  }, [onShowMap, detailHotel])

  const dayRangeLabel = useMemo(() => {
    if (!selectedOfferId) return undefined
    const range = getOfferItineraryDayRange(selectedOfferId, itineraryData)
    return range?.label
  }, [selectedOfferId, itineraryData])

  const detailOrderId = useMemo(
    () => getOfferOrderId(selectedOfferId ?? undefined, itineraryData),
    [selectedOfferId, itineraryData],
  )

  const detailHeaderActions = useMemo(() => {
    if (!isDetailView || !detailHotel) return undefined
    const hasGeo = !!detailHotel.geoLocation
    const showMapLabel = intl.formatMessage({
      id: 'uyDWABuVrlw97fTIbbjL',
      defaultMessage: 'Show map',
      description: 'Button label to show hotel location on map in hotel detail view',
    })
    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {hasGeo && (
          <FilterChip size="small" startIcon={<LineMapIcon />} onClick={handleShowMapFromHeader}>
            {showMapLabel}
          </FilterChip>
        )}
        {dayRangeLabel && (
          <NonInteractiveChipWrapper>
            <FilterChip size="small">
              {dayRangeLabel}
            </FilterChip>
          </NonInteractiveChipWrapper>
        )}
      </Group>
    )
  }, [isDetailView, detailHotel, handleShowMapFromHeader, dayRangeLabel, intl])

  const askAIFooter = useMemo(() => {
    if (!isDetailView || !detailHotel) return undefined
    return (
      <DrawerAskAIFooter
        contextLabel={detailHotel.name}
        isOpen={isOpen}
        hotelDetails={formatDrawerHotelInfo(detailHotel)}
      />
    )
  }, [isDetailView, detailHotel, isOpen])

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={handleClose}
      onAfterClose={handleAfterClose}
      onBack={isDetailView ? handleBack : undefined}
      direction="right-to-left"
      footer={askAIFooter}
      headerActions={detailHeaderActions}
    >
      {isDetailView && (
        <>
          {isDetailLoading && !detailHotel && (
            <ContentWrapper>
              <Group direction="vertical" gap={16}>
                <FullWidthLoadingBox height={200} borderRadius="M" />
                <LoadingBox height={28} width={240} borderRadius="S" />
                <LoadingBox height={20} width={160} borderRadius="S" />
              </Group>
            </ContentWrapper>
          )}
          {isDetailError && !isDetailLoading && !detailHotel && (
            <ContentWrapper>
              <Group direction="vertical" gap={8} horizontalAlign="center" verticalAlign="center">
                <BodyText variant="medium" colour="critical">
                  {intl.formatMessage({
                    id: 'AyMdUB-fKPPyurSXTes2',
                    defaultMessage: 'Failed to load hotel details.',
                    description: 'Error message shown when hotel offer details fail to load in the drawer',
                  })}
                </BodyText>
                <TextButton kind="secondary" size="small" onClick={() => refetchDetail()}>
                  {intl.formatMessage({
                    id: 'q2nIV5-Sco41UZtxNQNg',
                    defaultMessage: 'Try again',
                    description: 'Button label to retry loading hotel offer details after an error',
                  })}
                </TextButton>
              </Group>
            </ContentWrapper>
          )}
          {detailHotel && (
            <HotelDetailView
              hotel={detailHotel}
              itineraryData={itineraryData}
              onSendMessage={onSendMessage}
              reviews={reviews}
              reviewsLoading={reviewsLoading}
              isBooked={isOfferBookedInItinerary(selectedOfferId ?? undefined, itineraryData)}
              orderId={detailOrderId}
            />
          )}
        </>
      )}

      {!isDetailView && (
        <ContentWrapper>
          <Group direction="vertical" gap={24}>
            <HotelListingDrawerHeader
              placeName={placeName}
              placeCountry={placeCountry}
              onExploreMap={onExploreMap}
            />

            {isLoading && (
              <Group direction="vertical" gap={16}>
                {Array.from({ length: SKELETON_COUNT }, (_, index) => (
                  <DrawerHotelTileSkeleton key={index} />
                ))}
              </Group>
            )}

            {!isLoading && !hasHotels && (
              <HotelListingEmptyState placeName={placeName} searchUrl={browseAllResultsUrl} />
            )}

            {!isLoading && hasHotels && (
              <>
                <Group direction="vertical" gap={16}>
                  {hotels.map((hotel) => (
                    <DrawerHotelTile
                      key={hotel.id}
                      hotel={hotel}
                      onSelectHotel={handleSelectHotel}
                      itineraryData={itineraryData}
                      onSendMessage={onSendMessage}
                      onShowOnMap={onShowMap}
                    />
                  ))}
                </Group>

                <Sheet
                  padding={20}
                  background="neutral-seven"
                  border="neutral-five"
                  borderRadius="M"
                >
                  <Group
                    direction="horizontal"
                    horizontalAlign="space-between"
                    verticalAlign="center"
                  >
                    <Group direction="vertical" gap={4}>
                      <Heading variant="heading5">
                        {intl.formatMessage({
                          id: 'ru4Waq-H2PUMSkqeZtql',
                          defaultMessage: 'There’s plenty more to explore',
                          description: 'Heading encouraging users to browse more hotel search results',
                        })}
                      </Heading>
                      <BodyText variant="medium" colour="neutral-two">
                        {intl.formatMessage({
                          id: 'XRQo5Ud6rpNAM9E_4biT',
                          defaultMessage: 'We’ve got many more options available — view all search results.',
                          description: 'Body text encouraging users to view all hotel search results',
                        })}
                      </BodyText>
                    </Group>
                    <TextButton
                      kind="secondary"
                      size="medium"
                      endIcon={<LineExternalLinkAltIcon />}
                      href={browseAllResultsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {intl.formatMessage({
                        id: 'Itdw8U-Te31904tk2WhR',
                        defaultMessage: 'Browse all results',
                        description: 'Button label to browse all hotel search results on main site',
                      })}
                    </TextButton>
                  </Group>
                </Sheet>
              </>
            )}
          </Group>
        </ContentWrapper>
      )}
    </TripDrawer>
  )
}

export default HotelListingDrawer
